import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';

import { getUserActivity } from '../../api/Api';
import ActivityCalendar, { ActivityDay, ActivityRange } from '../../shared/ActivityCalendar';
import GlowCard from '../../shared/GlowCard';
import { useColors } from '../../theme/ColorModeContext';
import { plural, streakDeadline, streakMessage } from './streak';

/** Response of GET /user/activity (intel.own_activity). */
export interface OwnActivity {
    activity: ActivityRange;
    current_streak: number;
    longest_streak: number;
    learned_today: boolean;
    learning_days: number;
}

const MONO = '"Share Tech Mono", monospace';

const fmtDay = (date: string) =>
    new Date(`${date}T00:00:00Z`).toLocaleDateString([], { month: 'short', day: 'numeric', timeZone: 'UTC' });

const describeDay = (date: string, day: ActivityDay | undefined) => {
    if (!day) return `${fmtDay(date)}: nothing yet`;
    const parts = [
        day.challenge_runs ? plural(day.challenge_runs, 'run') : '',
        day.theory_answers ? plural(day.theory_answers, 'answer') : '',
        day.solves ? plural(day.solves, 'solve') : '',
    ].filter(Boolean);
    return `${fmtDay(date)}: ${parts.length ? parts.join(' · ') : 'signed in'}`;
};

const ActivityCard = ({ token }: { token: string }) => {
    const colors = useColors();
    const [data, setData] = useState<OwnActivity | null>(null);

    useEffect(() => {
        let cancelled = false;
        getUserActivity(token).then((result) => {
            if (!cancelled && result) setData(result);
        });
        return () => {
            cancelled = true;
        };
    }, [token]);

    if (!data) return null;

    const onStreak = data.current_streak > 0;
    const atRisk = onStreak && !data.learned_today;
    const flame = onStreak ? colors.accent.orange : colors.text.muted;

    return (
        <GlowCard glowOnHover={false} sx={{ padding: '20px 24px', marginBottom: '24px' }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 3, md: 5 }, alignItems: { md: 'center' } }}>
                {/* Streak */}
                <Box sx={{ flex: { md: '0 0 260px' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <LocalFireDepartmentIcon sx={{
                            fontSize: '40px',
                            color: flame,
                            filter: onStreak ? `drop-shadow(0 0 8px ${flame})` : 'none',
                            animation: atRisk ? 'glowPulse 2s ease-in-out infinite' : 'none',
                        }} />
                        <Box>
                            <Typography sx={{ fontFamily: '"Orbitron", sans-serif', fontSize: '28px', fontWeight: 700, lineHeight: 1, color: onStreak ? colors.text.primary : colors.text.muted }}>
                                {data.current_streak}
                            </Typography>
                            <Typography sx={{ fontFamily: MONO, fontSize: '10px', color: colors.text.muted, textTransform: 'uppercase', letterSpacing: '0.1em', mt: '4px' }}>
                                Day streak
                            </Typography>
                        </Box>
                    </Box>
                    <Typography sx={{ fontSize: '13px', color: atRisk ? colors.accent.orange : colors.text.secondary, mt: 1.5, lineHeight: 1.5 }}>
                        {streakMessage(data, streakDeadline())}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3, mt: 1.5 }}>
                        {[
                            { label: 'Best streak', value: plural(data.longest_streak, 'day') },
                            { label: 'Learning days', value: data.learning_days },
                        ].map((stat) => (
                            <Box key={stat.label}>
                                <Typography sx={{ fontFamily: MONO, fontSize: '14px', color: colors.text.primary, lineHeight: 1 }}>{stat.value}</Typography>
                                <Typography sx={{ fontFamily: MONO, fontSize: '9px', color: colors.text.muted, textTransform: 'uppercase', letterSpacing: '0.08em', mt: '3px' }}>{stat.label}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Calendar */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontFamily: '"Orbitron", sans-serif', fontSize: '13px', letterSpacing: '0.05em', textTransform: 'uppercase', color: colors.text.secondary, mb: 0.5 }}>
                        Activity
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: colors.text.muted, mb: 1.5 }}>
                        Darker means more happened that day: challenge runs, answers and solves, plus a mark for being signed in.
                    </Typography>
                    <ActivityCalendar activity={data.activity} describe={describeDay} />
                </Box>
            </Box>
        </GlowCard>
    );
};

export default ActivityCard;
